import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Clock } from "lucide-react";
import { useApp } from "@/context/AppContext";

// Countdown to next midnight (local). Shows HH : MM : SS.
function useMidnightCountdown() {
  const calc = () => {
    const now = new Date();
    const end = new Date(now);
    end.setHours(24, 0, 0, 0);
    const diff = Math.max(0, end.getTime() - now.getTime());
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    return { h, m, s };
  };
  const [t, setT] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => setT(calc()), 1000);
    return () => clearInterval(id);
  }, []);
  return t;
}

const pad = (n) => String(n).padStart(2, "0");

// Collapse on scroll-down, expand on scroll-up. Manual override supported.
function useScrollCollapse() {
  const [collapsed, setCollapsed] = useState(false);
  const [manual, setManual] = useState(false);
  const lastY = useRef(0);
  useEffect(() => {
    lastY.current = window.scrollY;
    const onScroll = () => {
      if (manual) return;
      const y = window.scrollY;
      const delta = y - lastY.current;
      if (Math.abs(delta) < 6) return;
      if (delta > 0 && y > 40) setCollapsed(true);
      else if (delta < 0) setCollapsed(false);
      lastY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [manual]);
  const toggle = () => {
    setManual(true);
    setCollapsed((c) => !c);
    window.setTimeout(() => setManual(false), 1500);
  };
  return { collapsed, toggle };
}

export default function TopPromo() {
  const { h, m, s } = useMidnightCountdown();
  const { t, formatPrice } = useApp();
  const { collapsed, toggle } = useScrollCollapse();

  return (
    <div
      data-testid="top-promo-bar"
      className="relative w-full bg-[#FFFDF6] text-black border-b border-black/10"
    >
      <AnimatePresence mode="wait" initial={false}>
        {collapsed ? (
          <motion.button
            key="collapsed"
            data-testid="promo-collapsed-toggle"
            onClick={toggle}
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="flex w-full items-center justify-center py-1.5"
          >
            <span className="inline-flex items-center gap-2 h-8 px-4 rounded-full bg-white border border-black/15 shadow-[0_2px_10px_rgba(0,0,0,0.06)] text-black text-[13px] font-display font-bold tracking-wide hover:shadow-[0_4px_14px_rgba(0,0,0,0.1)] transition-shadow">
              <Clock size={13} strokeWidth={2.4} />
              <span style={{ fontVariantNumeric: "tabular-nums" }}>
                {pad(h)}:{pad(m)}:{pad(s)}
              </span>
              <span className="text-[#E03030] text-[11px] tracking-[0.16em] uppercase font-bold ml-1">
                {t("promo.launch_title")}
              </span>
            </span>
          </motion.button>
        ) : (
          <motion.div
            key="expanded"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-[1680px] mx-auto px-4 md:px-8 py-2 md:py-2.5"
          >
            <div
              onClick={toggle}
              className="grid grid-cols-1 md:grid-cols-[1.4fr_auto_1fr] items-center gap-3 md:gap-6 cursor-pointer select-none"
            >
              {/* LEFT: launch offer copy */}
              <div data-testid="promo-launch-copy" className="min-w-0">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <span className="text-[10px] md:text-[11px] tracking-[0.24em] uppercase font-black text-[#E03030]">
                    {t("promo.launch_title")}
                  </span>
                  <span className="text-[12px] md:text-[14px] font-display font-black text-black uppercase tracking-tight leading-tight">
                    {t("promo.launch_desc_prefix")} {formatPrice(150, { base: "GBP", round: 5 })}
                  </span>
                </div>
                <p className="hidden md:block mt-1 text-[11px] md:text-[12px] text-black/70 font-medium leading-snug max-w-md">
                  {t("promo.launch_sub")}
                </p>
              </div>

              {/* CENTRE: price + save badge */}
              <div
                data-testid="promo-price-block"
                className="flex items-center gap-2 md:gap-3 justify-start md:justify-center"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="text-[11px] md:text-[13px] text-black/50 font-semibold uppercase tracking-[0.1em] whitespace-nowrap">
                  {t("promo.was")} <span className="line-through decoration-[1.5px] decoration-black/60">{formatPrice(300, { base: "GBP", round: 5 })}</span>
                </span>
                <span className="font-display font-black text-[18px] md:text-[22px] leading-none tracking-tight text-black whitespace-nowrap">
                  {t("promo.now")} {formatPrice(150, { base: "GBP", round: 5 })}
                </span>
                <span
                  data-testid="promo-save-badge"
                  className="inline-flex items-center h-6 md:h-7 px-2.5 rounded-full bg-[#0FA958] text-white text-[10px] md:text-[11px] font-black tracking-[0.14em] uppercase whitespace-nowrap shadow-[0_2px_8px_rgba(15,169,88,0.35)]"
                >
                  {t("promo.save")}
                </span>
              </div>

              {/* RIGHT: countdown */}
              <div data-testid="promo-timer" className="flex items-center gap-2 md:gap-3 justify-start md:justify-end min-w-0">
                <div className="flex items-center gap-1.5 text-[10px] md:text-[11px] tracking-[0.18em] uppercase font-bold whitespace-nowrap text-black/80">
                  <Clock size={13} strokeWidth={2.2} />
                  <span className="hidden sm:inline">{t("promo.offer_ends_in")}</span>
                </div>
                <div
                  className="flex items-center gap-1.5 md:gap-2 font-display font-black leading-none"
                  style={{ fontVariantNumeric: "tabular-nums" }}
                >
                  <TimeBlock v={pad(h)} label={t("promo.hrs")} />
                  <span className="text-black/40 -mt-1">:</span>
                  <TimeBlock v={pad(m)} label={t("promo.mins")} />
                  <span className="text-black/40 -mt-1">:</span>
                  <TimeBlock v={pad(s)} label={t("promo.secs")} />
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function TimeBlock({ v, label }) {
  return (
    <div className="text-center leading-none">
      <div className="font-display font-black text-[15px] md:text-[19px] tracking-tight">{v}</div>
      <div className="text-[8px] md:text-[9px] tracking-[0.22em] uppercase text-black/70 mt-1 font-semibold">
        {label}
      </div>
    </div>
  );
}
